import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingField,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { clearDiagramUndoHistory } from 'src/containers/ProjectDiagram/utils/clearDiagramUndoHistory';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { createDraftData } from 'src/containers/ProjectDiagram/utils/draft/createDraftData';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addMapping } from 'src/containers/ProjectDiagram/utils/mapping/addMapping';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { ProjectResourceSuggestionsData } from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectNodeDataArraySelector } from 'src/selectors/projectNodeDataArray';
import { projectResourcesByIdSelector } from 'src/selectors/projectResource';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useNavigateMapping } from 'src/utils/hooks/useNavigateMapping';
import { usePortalConnectionsWithDemo } from 'src/utils/hooks/usePortalConnectionsWithDemo';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useCommitOneResourceOneClassCallback = () => {
  const diagram = useDiagram();
  const navigateMapping = useNavigateMapping();
  const defaultPortalConnection = useDefaultPortalConnection();
  const portalConnections = usePortalConnectionsWithDemo();

  const { projectId } = useRouterParams();

  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));

  const resourcesById = useReduxSelector(
    projectResourcesByIdSelector(projectId)
  );
  const nodeDataArray = useReduxSelector(
    projectNodeDataArraySelector(projectId)
  );

  return React.useCallback(
    async ({
      mappingName,
      selectedClasses,
      selectedResources,
    }: Pick<
      ProjectResourceSuggestionsData,
      'selectedClasses' | 'selectedResources'
    > & { mappingName?: string }) => {
      if (!defaultPortalConnection || !portalConnections || !diagram) {
        return;
      }

      const resourceId = selectedResources[0];
      const resource = resourcesById[resourceId];
      const classIri = selectedClasses[0];
      const classLabel = getLabelForIri(labelsByIri, classIri);

      // always create a new mapping for one-to-one case
      const value = uuid();
      const nodeData = nodeDataArray.find(
        (nodeData): nodeData is Resource =>
          isResource(nodeData) && nodeData.key === getResourceKey(resourceId)
      );
      if (!nodeData) {
        return;
      }
      const fields = nodeData.fields.reduce<Record<string, MappingField>>(
        (acc, { label, value }) => {
          acc[value] = { label, value, targets: [] };
          return acc;
        },
        {}
      );

      if (!diagram.model.modelData.draftData) {
        // clear undo history for new draft
        clearDiagramUndoHistory(diagram, projectId);
      }
      commitDiagramModel(
        diagram,
        (model, diagram) => {
          // setup draft data
          if (!model.modelData.draftData) {
            const draftData = createDraftData(model);
            setDataProperty<ModelData>(model)(
              model.modelData,
              'draftData',
              draftData
            );
          }

          addMapping({
            fromId: resourceId,
            toKey: getModelConceptClassKey(classIri),
            text: mappingName || `${resource.name} - ${classLabel}`,
            fields,
            value,
          })(model, diagram);
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );
      // navigates to the mapping page and runs the suggestions, so no need to
      // dispatch the added suggestions to project action here
      navigateMapping(value, true);
    },
    [
      defaultPortalConnection,
      diagram,
      labelsByIri,
      navigateMapping,
      nodeDataArray,
      portalConnections,
      projectId,
      resourcesById,
    ]
  );
};
